/**
 * Responsive Sidebar Component
 * Adapts between mobile drawer, tablet collapsible, and desktop persistent sidebar
 */

import React from 'react'
import { HiX } from 'react-icons/hi'

export interface ResponsiveSidebarProps {
  content?: React.ReactNode
  isOpen: boolean
  onClose: () => void
  variant?: 'mobile' | 'tablet' | 'desktop'
  className?: string
}

export const ResponsiveSidebar: React.FC<ResponsiveSidebarProps> = ({
  content,
  isOpen,
  onClose,
  variant = 'desktop',
  className = ''
}) => {
  // Mobile: Full-screen overlay sidebar
  if (variant === 'mobile') {
    return (
      <>
        {/* Backdrop */}
        <div
          className={`
            fixed inset-0 bg-black transition-opacity duration-300 z-40
            ${isOpen ? 'bg-opacity-50 pointer-events-auto' : 'bg-opacity-0 pointer-events-none'}
          `}
          onClick={onClose}
        />

        {/* Sidebar Panel */}
        <aside
          className={`
            fixed left-0 top-0 h-full w-72 max-w-[80vw] bg-white shadow-xl z-50
            transform transition-transform duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            ${className}
          `}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Menu</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close sidebar"
            >
              <HiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {content}
          </div>
        </aside>
      </>
    )
  }

  // Tablet: Collapsible sidebar
  if (variant === 'tablet') {
    return (
      <aside
        className={`
          bg-white border-r shadow-sm transition-all duration-300
          ${isOpen ? 'w-64' : 'w-0'}
          ${className}
        `}
        style={{ overflow: isOpen ? 'visible' : 'hidden' }}
      >
        {/* Toggle Button */}
        <button
          onClick={onClose}
          className={`
            absolute top-4 -right-10 p-2 bg-white rounded-r-lg shadow-md
            hover:bg-gray-50 transition-colors
            ${!isOpen ? 'hidden' : ''}
          `}
          aria-label="Toggle sidebar"
        >
          <HiX className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className={`${isOpen ? 'p-4' : 'p-0'} transition-all duration-300`}>
          {isOpen && content}
        </div>
      </aside>
    )
  }

  // Desktop: Persistent sidebar
  return (
    <aside className={`bg-white h-full ${className}`}>
      <div className="p-4">
        {content}
      </div>
    </aside>
  )
}

export default ResponsiveSidebar